import React, { useState, useEffect, useMemo } from "react";
import { useInstantSearch } from "react-instantsearch";
import { Dialog, DialogTitle, DialogContent, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Mapping des champs dont le nom d'agrégation diffère de l'attribut
const AGG_FIELD_MAP = { collaborateurs: "collaborateurs.keyword" };

// Corps — fetch direct OpenSearch, ZÉRO interférence avec InstantSearch
function FacetModalBody({
  attribute,
  searchable,
  searchablePlaceholder,
  extractLabel,
  labelMap,
  selected,
  setSelected,
  modalLimit,
  onTotalCount,
  active,
}) {
  const { indexUiState } = useInstantSearch();
  const [rawItems, setRawItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const aggField = AGG_FIELD_MAP[attribute] || attribute;

  // Fetch indépendant : ne tourne QUE quand active=true (modale ouverte)
  useEffect(() => {
    if (!active) return;

    // Construire les filtres depuis indexUiState (hors cet attribut)
    // Regroupement : valeurs multiples d'un même attribut → OR (bool.should)
    const filters = [];
    const rf = indexUiState.refinementList || {};
    for (const [attr, values] of Object.entries(rf)) {
      if (attr === attribute || !values || values.length === 0) continue;
      const pos = [];
      const neg = [];
      for (const v of values) {
        if (typeof v === "string" && v.startsWith("NOT:")) {
          neg.push({ term: { [attr]: v.slice(4) } });
        } else {
          pos.push({ term: { [attr]: v } });
        }
      }
      if (pos.length === 1) {
        filters.push(pos[0]);
      } else if (pos.length > 1) {
        filters.push({ bool: { should: pos, minimum_should_match: 1 } });
      }
      if (neg.length > 0) {
        filters.push({ bool: { must_not: neg } });
      }
    }
    const range = indexUiState.range || {};
    for (const [attr, bounds] of Object.entries(range)) {
      const r = {};
      if (bounds.min != null) r.gte = bounds.min;
      if (bounds.max != null) r.lte = bounds.max;
      if (Object.keys(r).length > 0) filters.push({ range: { [attr]: r } });
    }
    const mustClauses = [];
    const queryText = indexUiState.query || "";
    if (queryText.trim()) {
      mustClauses.push({ multi_match: { query: queryText, fields: ["full_text", "titre", "cote.text", "auteur.text"] } });
    }
    const boolQuery = {};
    if (mustClauses.length > 0) boolQuery.must = mustClauses;
    if (filters.length > 0) boolQuery.filter = filters;
    const queryClause = Object.keys(boolQuery).length > 0 ? { bool: boolQuery } : { match_all: {} };

    const body = JSON.stringify({
      size: 0,
      query: queryClause,
      aggs: { facet: { terms: { field: aggField, size: modalLimit } } }
    });

    let cancelled = false;
    fetch("./search-proxy.php/memovs_av_v1/_search", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
    })
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        const buckets = data?.aggregations?.facet?.buckets || [];
        setRawItems(buckets.map(b => ({ value: b.key, label: b.key, count: b.doc_count })));
        if (onTotalCount) onTotalCount(buckets.length);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [active, indexUiState, attribute, aggField, modalLimit, onTotalCount]);

  const resolveLabel = (label) => {
    if (extractLabel) return extractLabel(label);
    if (labelMap && labelMap[label]) return labelMap[label];
    return label;
  };

  const sortedItems = useMemo(() => {
    const src = rawItems || [];
    return [...src].sort((a, b) =>
      resolveLabel(a.label).toLowerCase().localeCompare(resolveLabel(b.label).toLowerCase(), "fr")
    );
  }, [rawItems]);

  const filteredItems = searchQuery.trim() === ""
    ? sortedItems
    : sortedItems.filter((item) =>
        resolveLabel(item.label).toLowerCase().includes(searchQuery.toLowerCase())
      );

  const toggle = (value) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  return (
    <div>
      {searchable && (
        <div className="sui-facet-search" style={{ marginBottom: "12px" }}>
          <input
            className="sui-facet-search__text-input"
            type="search"
            placeholder={searchablePlaceholder || "Rechercher..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
        </div>
      )}
      <div className="sui-multi-checkbox-facet">
        {filteredItems.length === 0 && searchQuery && (
          <div style={{ fontSize: "14px", color: "#666", textAlign: "center", padding: "16px 0" }}>
            Aucun résultat pour « {searchQuery} »
          </div>
        )}
        {filteredItems.map((item) => (
          <label key={item.value} className="sui-multi-checkbox-facet__option-label">
            <div className="sui-multi-checkbox-facet__option-input-wrapper">
              <input
                type="checkbox"
                className="sui-multi-checkbox-facet__checkbox"
                checked={selected.has(item.value)}
                onChange={() => toggle(item.value)}
              />
              <span className="sui-multi-checkbox-facet__input-text">{resolveLabel(item.label)}</span>
            </div>
            <span className="sui-multi-checkbox-facet__option-count">
              {item.count.toLocaleString("fr-CH")}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// Shell — keepMounted, gère selected + flush au close
// ------------------------------------------------------------
export default function FacetModal({
  attribute,
  title,
  searchable,
  searchablePlaceholder,
  extractLabel,
  labelMap,
  open,
  onClose,
  modalLimit = 1000,
}) {
  const { indexUiState, setIndexUiState, refresh } = useInstantSearch();
  const [selected, setSelected] = useState(() => new Set());
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (open) {
      setSelected(new Set(indexUiState.refinementList?.[attribute] || []));
    }
  }, [open]);

  const handleClose = () => {
    setIndexUiState((prev) => {
      const arr = [...selected];
      return {
        ...prev,
        refinementList: {
          ...prev.refinementList,
          [attribute]: arr.length > 0 ? arr : undefined,
        },
      };
    });
    refresh();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" keepMounted>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
        <Typography variant="h6" component="span" sx={{ fontSize: "1rem" }}>
          {title} ({totalCount.toLocaleString("fr-CH")})
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: "70vh", overflow: "auto", pt: 1, display: open ? "block" : "none" }}>
        <FacetModalBody
          attribute={attribute}
          searchable={searchable}
          searchablePlaceholder={searchablePlaceholder}
          extractLabel={extractLabel}
          labelMap={labelMap}
          selected={selected}
          setSelected={setSelected}
          modalLimit={modalLimit}
          onTotalCount={setTotalCount}
          active={open}
        />
      </DialogContent>
    </Dialog>
  );
}
